<template>
    <div v-if="showFab" class="sip-pwa-install" :class="{ 'sip-pwa-install--solo': !aiWidgetEnabled }">
        <button
            type="button"
            class="sip-pwa-install__fab"
            :aria-label="installLabel"
            :title="installLabel"
            @click.prevent="onInstallClick"
        >
            <admin-icon name="download" :size="22"></admin-icon>
        </button>

        <transition name="sip-pwa-sheet">
            <div
                v-if="showIosSheet"
                class="sip-pwa-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby="sip-pwa-sheet-title"
                @click.self="closeIosSheet"
            >
                <div class="sip-pwa-sheet__card">
                    <div class="sip-pwa-sheet__head">
                        <div class="sip-pwa-sheet__brand">
                            <img v-if="logoUrl" class="sip-pwa-sheet__logo" :src="logoUrl" :alt="appName">
                            <div>
                                <h2 id="sip-pwa-sheet-title" class="sip-pwa-sheet__title">Install {{ appName }}</h2>
                                <p class="sip-pwa-sheet__sub">Add this app to your Home Screen</p>
                            </div>
                        </div>
                        <button type="button" class="sip-pwa-sheet__close" aria-label="Close" @click="closeIosSheet">
                            <admin-icon name="close" :size="16"></admin-icon>
                        </button>
                    </div>
                    <ol class="sip-pwa-sheet__steps">
                        <li>
                            <span class="sip-pwa-sheet__num">1</span>
                            <span class="sip-pwa-sheet__copy">
                                Tap
                                <span class="sip-pwa-sheet__share" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M12 16V4"/>
                                        <path d="M8 8l4-4 4 4"/>
                                        <path d="M6 14v5a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-5"/>
                                    </svg>
                                </span>
                                <strong>Share</strong>
                            </span>
                        </li>
                        <li>
                            <span class="sip-pwa-sheet__num">2</span>
                            <span class="sip-pwa-sheet__copy">Tap <strong>Add to Home Screen</strong></span>
                        </li>
                        <li>
                            <span class="sip-pwa-sheet__num">3</span>
                            <span class="sip-pwa-sheet__copy">Tap <strong>Add</strong> / <strong>Done</strong></span>
                        </li>
                    </ol>
                    <button type="button" class="sip-pwa-sheet__done" @click="closeIosSheet">Got it</button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: 'PwaInstallFab',
    data() {
        return {
            deferredPrompt: null,
            isStandalone: false,
            isIos: false,
            showIosSheet: false,
        };
    },
    computed: {
        appName() {
            const name = helper.getConfig && helper.getConfig('institute_name');
            const fallback = (helper.getConfig && helper.getConfig('app_name')) || 'Sipinor';
            const trimmed = name ? String(name).trim() : '';
            return trimmed || fallback || 'Sipinor';
        },
        logoUrl() {
            return helper.getLogo ? helper.getLogo() : '/api/configuration/logo/image';
        },
        aiWidgetEnabled() {
            return !!(helper.getConfig && helper.getConfig('ai_assistant_widget'));
        },
        installLabel() {
            return 'Install ' + this.appName;
        },
        showFab() {
            if (this.isStandalone) {
                return false;
            }
            return this.isIos || !!this.deferredPrompt;
        },
    },
    mounted() {
        this.isIos = this.detectIos();
        this.isStandalone = this.detectStandalone();
        this._onBeforeInstall = this.onBeforeInstall.bind(this);
        this._onAppInstalled = this.onAppInstalled.bind(this);
        this._onDisplayChange = this.syncStandalone.bind(this);
        window.addEventListener('beforeinstallprompt', this._onBeforeInstall);
        window.addEventListener('appinstalled', this._onAppInstalled);
        if (window.matchMedia) {
            this._standaloneMq = window.matchMedia('(display-mode: standalone)');
            if (this._standaloneMq.addEventListener) {
                this._standaloneMq.addEventListener('change', this._onDisplayChange);
            } else if (this._standaloneMq.addListener) {
                this._standaloneMq.addListener(this._onDisplayChange);
            }
        }
    },
    beforeDestroy() {
        window.removeEventListener('beforeinstallprompt', this._onBeforeInstall);
        window.removeEventListener('appinstalled', this._onAppInstalled);
        if (this._standaloneMq) {
            if (this._standaloneMq.removeEventListener) {
                this._standaloneMq.removeEventListener('change', this._onDisplayChange);
            } else if (this._standaloneMq.removeListener) {
                this._standaloneMq.removeListener(this._onDisplayChange);
            }
        }
    },
    methods: {
        detectIos() {
            const ua = window.navigator.userAgent || '';
            const iOS = /iPad|iPhone|iPod/.test(ua);
            const iPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
            return iOS || iPadOs;
        },
        detectStandalone() {
            if (window.navigator.standalone === true) {
                return true;
            }
            if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
                return true;
            }
            if (window.matchMedia && window.matchMedia('(display-mode: fullscreen)').matches) {
                return true;
            }
            if (window.matchMedia && window.matchMedia('(display-mode: minimal-ui)').matches) {
                return true;
            }
            return false;
        },
        syncStandalone() {
            this.isStandalone = this.detectStandalone();
            if (this.isStandalone) {
                this.showIosSheet = false;
                this.deferredPrompt = null;
            }
        },
        onBeforeInstall(e) {
            e.preventDefault();
            this.deferredPrompt = e;
        },
        onAppInstalled() {
            this.deferredPrompt = null;
            this.isStandalone = true;
            this.showIosSheet = false;
        },
        onInstallClick() {
            if (this.deferredPrompt && typeof this.deferredPrompt.prompt === 'function') {
                const promptEvent = this.deferredPrompt;
                this.deferredPrompt = null;
                promptEvent.prompt();
                const choice = promptEvent.userChoice;
                if (choice && typeof choice.then === 'function') {
                    choice.then(function (result) {
                        if (result && result.outcome === 'accepted') {
                            this.isStandalone = true;
                        }
                    }.bind(this)).catch(function () {});
                }
                return;
            }
            if (this.isIos) {
                this.showIosSheet = true;
            }
        },
        closeIosSheet() {
            this.showIosSheet = false;
        },
    },
};
</script>

<style scoped>
.sip-pwa-install {
    position: fixed;
    right: 90px;
    bottom: 22px;
    z-index: 10050;
    pointer-events: none;
}
.sip-pwa-install--solo {
    right: 22px;
}
.sip-pwa-install__fab {
    pointer-events: auto;
    width: 56px;
    height: 56px;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--sip-accent-fg, #ffffff);
    background: var(--sip-accent, #0284c7);
    box-shadow:
        0 4px 14px color-mix(in srgb, var(--sip-accent, #0284c7) 42%, transparent),
        0 0 0 1px rgba(255, 255, 255, 0.12) inset;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.sip-pwa-install__fab:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 22px color-mix(in srgb, var(--sip-accent, #0284c7) 52%, transparent);
}
.sip-pwa-sheet {
    pointer-events: auto;
    position: fixed;
    inset: 0;
    z-index: 10120;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 16px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    background: rgba(15, 23, 42, 0.45);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
}
.sip-pwa-sheet__card {
    width: min(420px, 100%);
    background: var(--sip-surface, #ffffff);
    color: var(--sip-text, #0f172a);
    border-radius: 18px;
    border: 1px solid var(--sip-line, #e2e8f0);
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
    padding: 18px 18px 16px;
}
.sip-pwa-sheet__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
}
.sip-pwa-sheet__brand {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}
.sip-pwa-sheet__logo {
    width: 44px;
    height: 44px;
    object-fit: contain;
    border-radius: 10px;
    background: var(--sip-bg, #f8fafc);
    border: 1px solid var(--sip-line, #e2e8f0);
}
.sip-pwa-sheet__title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.3;
}
.sip-pwa-sheet__sub {
    margin: 2px 0 0;
    font-size: 0.82rem;
    color: var(--sip-muted, #64748b);
}
.sip-pwa-sheet__close {
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--sip-muted, #64748b);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}
.sip-pwa-sheet__steps {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.sip-pwa-sheet__steps li {
    display: flex;
    align-items: center;
    gap: 10px;
}
.sip-pwa-sheet__num {
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.78rem;
    font-weight: 700;
    color: var(--sip-accent-fg, #fff);
    background: var(--sip-accent, #0284c7);
}
.sip-pwa-sheet__copy {
    font-size: 0.92rem;
    line-height: 1.4;
}
.sip-pwa-sheet__share {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    margin: 0 4px;
    border-radius: 6px;
    color: var(--sip-accent, #0284c7);
    background: var(--sip-accent-soft, color-mix(in srgb, var(--sip-accent, #0284c7) 12%, transparent));
    vertical-align: -4px;
}
.sip-pwa-sheet__done {
    display: block;
    width: 100%;
    margin-top: 16px;
    border: 0;
    border-radius: 12px;
    padding: 11px 14px;
    font-weight: 700;
    cursor: pointer;
    color: var(--sip-accent-fg, #fff);
    background: var(--sip-accent, #0284c7);
}
.sip-pwa-sheet-enter-active,
.sip-pwa-sheet-leave-active {
    transition: opacity 0.18s ease;
}
.sip-pwa-sheet-enter,
.sip-pwa-sheet-leave-to {
    opacity: 0;
}
@media (display-mode: standalone), (display-mode: fullscreen), (display-mode: minimal-ui) {
    .sip-pwa-install {
        display: none !important;
    }
}
</style>
